import { useMemo, useState } from 'react';
import { Bloque, Pagina, Tabla } from '../componentes/Estructura';
import { Cargando, ConError, Vacio } from '../componentes/Estados';
import { TIPO_VEHICULO } from '../componentes/Etiqueta';
import { dinero, laFechaHora } from '../componentes/formato';
import { hayBackend } from '../config/entorno';
import { useCargaReal } from '../dominio/carga';
import { ErrorApi } from '../dominio/clienteApi';
import { TIPOS_VEHICULO_TODOS } from '../dominio/contratos';
import type { TipoVehiculo } from '../dominio/contratos';
import {
  DECIMALES,
  MAX_FACTOR,
  MAX_MONTO,
  aNumero,
  esPublicacionConcurrente,
  esZonaInexistente,
  obtenerTarifariosVigentes,
  obtenerZonas,
  publicarTarifario,
} from '../dominio/tarifarioApi';
import type { PublicacionTarifario, TarifarioReal, ZonaReal } from '../dominio/tarifarioApi';

/**
 * Tarifario y zonas — DATO REAL (`GET /consola/tarifario`, `GET /consola/tarifario/zonas`,
 * `POST /consola/tarifario`). Ver `dominio/tarifarioApi.ts` para lo que se midió del backend.
 *
 * 🔴 **Es la única pantalla de la consola con camino de ESCRITURA a un dato que gobierna lo que
 * ve el pasajero**, y eso ordena tres decisiones de esta pantalla:
 *
 * 1. **Publicar no edita: crea una versión y cierra la anterior.** La pantalla lo dice antes de
 *    confirmar, con el número de versión que se va a cerrar. Quien publica tiene que saber que
 *    está dejando un rastro versionado, no pisando un valor.
 * 2. **Hay un paso de confirmación, y NO es `DialogoMotivo`.** `DialogoMotivo` existe para pedir
 *    un motivo que queda en la auditoría — y `Tarifario` **no tiene columna donde guardarlo**
 *    (`publicar-tarifario.dto.ts` lo declina explícitamente para no "aceptar un dato y perderlo en
 *    silencio"). Pedirlo acá sería exactamente eso: fricción que produce un texto que nadie va a
 *    poder leer nunca. La versión mock usaba `DialogoMotivo` porque no llamaba a nada. El paso de
 *    confirmación se conserva —muestra QUÉ se va a publicar y QUÉ se cierra— porque el valor de la
 *    fricción está ahí, no en el textarea.
 * 3. **El formulario valida ANTES de mandar, con las mismas reglas del DTO.** No para reemplazar
 *    al backend (que igual las hace cumplir, y contra todos los caminos, no sólo éste) sino para
 *    que el operador no descubra un tope de precisión de columna a través de un 400.
 */

/** El mensaje que ve el operador cuando algo falla. `ErrorApi` ya trae el motivo del backend. */
const mensajeDeFalla = (error: unknown): string =>
  error instanceof ErrorApi ? error.message : 'Ocurrió un error inesperado. Probá de nuevo.';

/**
 * Monto con su moneda, tolerante a una moneda que no sea un código ISO real.
 *
 * ⚠️ `Intl.NumberFormat` **lanza `RangeError`** con un `currency` que no sean 3 letras — y la
 * columna es `Char(3)` con `@Length(3,3)`, que admite `"1A2"` sin chistar. Sin este `catch`, una
 * fila con una moneda rara no rompe una celda: rompe el render de toda la pantalla.
 */
function importe(valor: string, moneda: string): string {
  const n = aNumero(valor);
  if (n === null) return '—';
  try {
    return dinero(n, moneda);
  } catch {
    return `${moneda} ${n.toFixed(DECIMALES)}`;
  }
}

/** El rango referencial que sale de los factores, que es lo que de verdad ve el pasajero. */
function rango(fila: TarifarioReal): string {
  const min = aNumero(fila.factorMin);
  const max = aNumero(fila.factorMax);
  if (min === null || max === null) return '—';
  return `×${min.toFixed(DECIMALES)} – ×${max.toFixed(DECIMALES)}`;
}

// ---------------------------------------------------------------------------
// Formulario de publicación
// ---------------------------------------------------------------------------

interface Formulario {
  tipoVehiculo: TipoVehiculo;
  /** `''` = tarifario GLOBAL (`zonaId` ausente en el cuerpo). */
  zonaId: string;
  moneda: string;
  banderazo: string;
  porKm: string;
  porMinuto: string;
  minimo: string;
  factorMin: string;
  factorMax: string;
}

type CampoNumerico = 'banderazo' | 'porKm' | 'porMinuto' | 'minimo' | 'factorMin' | 'factorMax';

type Errores = Partial<Record<keyof Formulario, string>>;

const MONTOS: { clave: CampoNumerico; etiqueta: string; ayuda: string }[] = [
  { clave: 'banderazo', etiqueta: 'Banderazo', ayuda: 'Lo fijo, antes de moverse.' },
  { clave: 'porKm', etiqueta: 'Por kilómetro', ayuda: 'Por cada km recorrido.' },
  { clave: 'porMinuto', etiqueta: 'Por minuto', ayuda: 'Por cada minuto de viaje.' },
  { clave: 'minimo', etiqueta: 'Mínimo', ayuda: 'Piso del cálculo, aunque el viaje sea corto.' },
];

const FACTORES: { clave: CampoNumerico; etiqueta: string; ayuda: string }[] = [
  { clave: 'factorMin', etiqueta: 'Factor mínimo', ayuda: 'Extremo bajo del rango, p.ej. 0.90.' },
  { clave: 'factorMax', etiqueta: 'Factor máximo', ayuda: 'Extremo alto del rango, p.ej. 1.20.' },
];

/**
 * El `<select>` entrega `string`. Se comprueba contra la lista del contrato compartido en vez de
 * castear: un `as TipoVehiculo` compilaría igual y dejaría de avisar el día que el enum crezca.
 */
const esTipoVehiculo = (valor: string): valor is TipoVehiculo =>
  (TIPOS_VEHICULO_TODOS as readonly string[]).includes(valor);

const FORMULARIO_VACIO: Formulario = {
  tipoVehiculo: 'AUTO',
  zonaId: '',
  moneda: 'PEN',
  banderazo: '',
  porKm: '',
  porMinuto: '',
  minimo: '',
  factorMin: '',
  factorMax: '',
};

/**
 * Lee un campo numérico del formulario replicando las reglas del DTO.
 *
 * ⚠️ **El caso vacío se trata PRIMERO, y no es una formalidad: `Number('')` es `0`, no `NaN`.**
 * Es la misma trampa que `config/entorno.ts` documenta con un caso que llegó a pantalla. Sin esta
 * guarda, dejar el banderazo en blanco no da "falta un valor": publica un banderazo de cero, que
 * es un número perfectamente plausible.
 */
function leerNumero(
  crudo: string,
  { minimoExclusivo, maximo }: { minimoExclusivo: boolean; maximo: number },
): { valor: number } | { error: string } {
  const texto = crudo.trim();
  if (texto === '') return { error: 'Falta el valor.' };
  const n = Number(texto);
  if (!Number.isFinite(n)) return { error: 'Tiene que ser un número. Usá punto decimal (1.20).' };
  if (minimoExclusivo && n <= 0) return { error: 'Tiene que ser mayor que cero.' };
  if (!minimoExclusivo && n < 0) return { error: 'No puede ser negativo.' };
  if (n > maximo) {
    return {
      error: `El máximo que admite la columna es ${maximo}. Más alto lo rechaza el backend.`,
    };
  }
  return { valor: n };
}

/**
 * Valida el formulario entero y arma el cuerpo del POST si está bien.
 *
 * Espejo de `PublicarTarifarioDto` (`@Min(0)`/`@Max(MAX_MONTO)` en los montos, `@IsPositive()`/
 * `@Max(MAX_FACTOR)` en los factores, `@Length(3,3)` en la moneda, `@NoMayorQue('factorMax')` en
 * el par). Función PURA a propósito: se puede ejercitar sin montar React el día que la consola
 * tenga runner de tests.
 */
export function validarFormulario(form: Formulario): {
  errores: Errores;
  cuerpo: PublicacionTarifario | null;
} {
  const errores: Errores = {};

  const moneda = form.moneda.trim().toUpperCase();
  if (moneda.length !== 3) {
    errores.moneda = 'Es un código ISO-4217 de 3 caracteres, por ejemplo PEN.';
  }

  const numeros: Partial<Record<CampoNumerico, number>> = {};
  for (const { clave } of MONTOS) {
    const leido = leerNumero(form[clave], { minimoExclusivo: false, maximo: MAX_MONTO });
    if ('error' in leido) errores[clave] = leido.error;
    else numeros[clave] = leido.valor;
  }
  for (const { clave } of FACTORES) {
    const leido = leerNumero(form[clave], { minimoExclusivo: true, maximo: MAX_FACTOR });
    if ('error' in leido) errores[clave] = leido.error;
    else numeros[clave] = leido.valor;
  }

  // El PAR. Cada factor puede ser válido por su cuenta y el par estar invertido — es justo lo que
  // `NoMayorQue` existe para cazar del lado del backend, y lo que el CHECK
  // `tarifarios_rango_factores_check` sostiene en el motor. Un rango invertido no falla en ningún
  // lado: produce `{min: 24, max: 18}` y se lo muestra al pasajero tal cual.
  const min = numeros.factorMin;
  const max = numeros.factorMax;
  if (min !== undefined && max !== undefined && min > max) {
    errores.factorMin = 'No puede ser mayor que el factor máximo: el rango quedaría invertido.';
  }

  if (Object.keys(errores).length > 0) return { errores, cuerpo: null };

  // Desestructurar y comprobar `undefined` uno por uno NO es redundante con el chequeo de arriba:
  // es lo que le prueba a `tsc` que los seis son `number`. La alternativa era un `as number` por
  // campo, que compila lo mismo y deja de avisar el día que se agregue un campo y nadie lo lea.
  const { banderazo, porKm, porMinuto, minimo, factorMin, factorMax } = numeros;
  if (
    banderazo === undefined ||
    porKm === undefined ||
    porMinuto === undefined ||
    minimo === undefined ||
    factorMin === undefined ||
    factorMax === undefined
  ) {
    return { errores, cuerpo: null };
  }

  const zonaId = form.zonaId.trim();
  return {
    errores,
    cuerpo: {
      tipoVehiculo: form.tipoVehiculo,
      // Ausente = global. Ver `PublicacionTarifario` en `dominio/tarifarioApi.ts`.
      ...(zonaId === '' ? {} : { zonaId }),
      moneda,
      banderazo,
      porKm,
      porMinuto,
      minimo,
      factorMin,
      factorMax,
    },
  };
}

/**
 * Estado de la publicación. `concurrente` no es decoración: el 409 se explica DISTINTO (nadie se
 * equivocó, hay que releer y volver a decidir) y además dispara una recarga.
 */
type EstadoPublicacion =
  | { estado: 'inactivo' }
  | { estado: 'confirmando'; cuerpo: PublicacionTarifario }
  | { estado: 'enviando'; cuerpo: PublicacionTarifario }
  | { estado: 'listo'; resultado: TarifarioReal }
  | { estado: 'error'; mensaje: string; concurrente: boolean };

// ---------------------------------------------------------------------------

export function Tarifario() {
  const [carga, recargar] = useCargaReal(async () => {
    const [tarifarios, zonas] = await Promise.all([obtenerTarifariosVigentes(), obtenerZonas()]);
    return { tarifarios, zonas };
  }, []);

  const [form, setForm] = useState<Formulario>(FORMULARIO_VACIO);
  const [mostrarErrores, setMostrarErrores] = useState(false);
  const [publicacion, setPublicacion] = useState<EstadoPublicacion>({ estado: 'inactivo' });

  const datos = carga.estado === 'listo' ? carga.datos : null;
  const tarifarios = datos?.tarifarios ?? [];
  const zonas = datos?.zonas ?? [];

  const nombreDeZona = useMemo(() => {
    const porId = new Map<string, ZonaReal>(zonas.map((z) => [z.id, z]));
    return (id: string | null): string => {
      if (id === null) return 'Global';
      return porId.get(id)?.nombre ?? `zona desconocida (${id})`;
    };
  }, [zonas]);

  const { errores, cuerpo } = validarFormulario(form);
  const hayErrores = Object.keys(errores).length > 0;

  /** La fila vigente que esta publicación va a CERRAR, si existe. */
  const vigenteQueSeCierra = useMemo(() => {
    const zonaId = form.zonaId.trim() === '' ? null : form.zonaId.trim();
    return (
      tarifarios.find((t) => t.tipoVehiculo === form.tipoVehiculo && t.zonaId === zonaId) ?? null
    );
  }, [tarifarios, form.tipoVehiculo, form.zonaId]);

  /**
   * Genérico en la clave para que `tipoVehiculo` no acepte un `string` cualquiera: el `<select>`
   * entrega `string` y el guard de abajo es lo que lo convierte en `TipoVehiculo`.
   */
  function escribir<K extends keyof Formulario>(clave: K, valor: Formulario[K]): void {
    setForm((previo) => ({ ...previo, [clave]: valor }));
    // Un resultado viejo al lado de un formulario que ya se está editando confunde: se limpia al
    // primer cambio, no al enviar.
    setPublicacion({ estado: 'inactivo' });
  }

  function pedirConfirmacion(): void {
    setMostrarErrores(true);
    if (!cuerpo) return;
    setPublicacion({ estado: 'confirmando', cuerpo });
  }

  async function confirmar(aPublicar: PublicacionTarifario): Promise<void> {
    setPublicacion({ estado: 'enviando', cuerpo: aPublicar });
    try {
      const resultado = await publicarTarifario(aPublicar);
      setPublicacion({ estado: 'listo', resultado });
      setMostrarErrores(false);
      // La tabla de arriba tiene que reflejar la versión nueva: sin esto queda mostrando la que
      // se acaba de cerrar, que es exactamente el dato que ya no vale.
      recargar();
    } catch (error) {
      if (esPublicacionConcurrente(error)) {
        // 🔴 No es un fallo del operador ni un problema de la pantalla: otra publicación sobre la
        // MISMA (clase, zona) llegó primero y el índice único parcial la dejó ganar. Se recarga
        // para que lo vigente en pantalla sea lo que quedó, y se CONSERVAN los valores del
        // formulario — quien publicó tiene que poder mirar lo nuevo y decidir, no volver a tipear.
        setPublicacion({
          estado: 'error',
          concurrente: true,
          mensaje: mensajeDeFalla(error),
        });
        recargar();
        return;
      }
      if (esZonaInexistente(error)) {
        setPublicacion({
          estado: 'error',
          concurrente: false,
          mensaje: `${mensajeDeFalla(error)} La lista de zonas de esta pantalla quedó vieja: se está recargando.`,
        });
        recargar();
        return;
      }
      setPublicacion({ estado: 'error', concurrente: false, mensaje: mensajeDeFalla(error) });
    }
  }

  const enviando = publicacion.estado === 'enviando';

  function campoNumerico({
    clave,
    etiqueta,
    ayuda,
  }: {
    clave: CampoNumerico;
    etiqueta: string;
    ayuda: string;
  }) {
    const error = mostrarErrores ? errores[clave] : undefined;
    return (
      <label className="campo" key={clave}>
        <span className="campo__etiqueta rotulo">{etiqueta}</span>
        <input
          className="entrada"
          type="text"
          inputMode="decimal"
          value={form[clave]}
          disabled={enviando}
          onChange={(evento) => escribir(clave, evento.target.value)}
        />
        <span className="campo__ayuda" style={error ? { color: 'var(--rojo)' } : undefined}>
          {error ?? ayuda}
        </span>
      </label>
    );
  }

  return (
    <Pagina
      rotulo="Solo administrador"
      titulo="Tarifario y zonas"
      ayuda="Los números con los que se calcula el rango de precio que se le muestra al pasajero antes de pedir el viaje."
    >
      {!hayBackend ? (
        <div className="bloque">
          <Vacio
            titulo="Sin backend configurado"
            detalle="Falta VITE_API_URL. Esta pantalla lee y ESCRIBE el tarifario real; sin backend no hay nada que mostrar, y no se inventan datos de ejemplo para una pantalla que publica."
          />
        </div>
      ) : (
        <>
          {/*
            🔴 La tarifa es REFERENCIAL: la plataforma no cobra el viaje ni toca el dinero (el
            modelo es tarifa plana mensual al conductor). Estos valores producen un rango
            orientativo, no un importe a cobrar. Decirlo en la pantalla no es un detalle legal de
            adorno: quien edite esto tiene que saber que está moviendo una expectativa, no una
            factura.
          */}
          <Bloque
            titulo="Tarifa referencial vigente"
            nota="Una sola versión vigente por clase de vehículo y zona: publicar cierra la anterior. La plataforma no cobra el viaje — el pago es entre pasajero y conductor."
            sinRelleno
          >
            {carga.estado === 'cargando' ? (
              <Cargando filas={4} etiqueta="Cargando tarifario" />
            ) : carga.estado === 'error' ? (
              <ConError detalle={carga.mensaje} alReintentar={recargar} />
            ) : tarifarios.length === 0 ? (
              <Vacio
                titulo="No hay ningún tarifario vigente"
                detalle="Sin tarifario, el pasajero no ve estimación. Publicá uno con el formulario de abajo."
              />
            ) : (
              <Tabla
                cabeceras={[
                  'Clase',
                  'Zona',
                  { texto: 'Versión', num: true },
                  { texto: 'Banderazo', num: true },
                  { texto: 'Por kilómetro', num: true },
                  { texto: 'Por minuto', num: true },
                  { texto: 'Mínimo', num: true },
                  { texto: 'Rango', num: true },
                  'Vigente desde',
                ]}
              >
                {tarifarios.map((fila) => (
                  <tr key={fila.id}>
                    <td>{TIPO_VEHICULO[fila.tipoVehiculo]}</td>
                    <td className={fila.zonaId === null ? 'tenue' : undefined}>
                      {nombreDeZona(fila.zonaId)}
                    </td>
                    <td className="tabla__num">v{fila.version}</td>
                    <td className="tabla__num">{importe(fila.banderazo, fila.moneda)}</td>
                    <td className="tabla__num">{importe(fila.porKm, fila.moneda)}</td>
                    <td className="tabla__num">{importe(fila.porMinuto, fila.moneda)}</td>
                    <td className="tabla__num">{importe(fila.minimo, fila.moneda)}</td>
                    <td className="tabla__num">{rango(fila)}</td>
                    <td className="tenue">{laFechaHora(fila.vigenteDesde)}</td>
                  </tr>
                ))}
              </Tabla>
            )}
          </Bloque>

          {/*
            La versión anterior de esta pantalla mostraba una columna "Recargo %" por zona. Ese
            número NO EXISTE en el backend: `model Zona` tiene id/nombre/poligono/activa y nada
            más (ver `dominio/tarifarioApi.ts`). El recargo por zona se expresa publicando un
            tarifario CON `zonaId`, que es lo que muestra la columna de abajo.
          */}
          <Bloque
            titulo="Zonas"
            nota="Una zona no lleva un porcentaje de recargo: lo que la diferencia es tener su propio tarifario publicado. Sin tarifario propio, adentro de la zona rige el global."
            sinRelleno
          >
            {carga.estado === 'cargando' ? (
              <Cargando filas={3} etiqueta="Cargando zonas" />
            ) : carga.estado === 'error' ? (
              <ConError detalle={carga.mensaje} alReintentar={recargar} />
            ) : zonas.length === 0 ? (
              <Vacio
                titulo="No hay zonas cargadas"
                detalle="Las zonas se cargan fuera de esta consola: no hay endpoint de alta ni de edición todavía."
              />
            ) : (
              <Tabla cabeceras={['Zona', 'Estado', 'Tarifario propio vigente']}>
                {zonas.map((zona) => {
                  const propios = tarifarios.filter((t) => t.zonaId === zona.id);
                  return (
                    <tr key={zona.id} data-estado={zona.activa ? 'bueno' : undefined}>
                      <td>
                        {zona.nombre}
                        <div className="apagado">{zona.id}</div>
                      </td>
                      <td className={zona.activa ? '' : 'apagado'}>
                        {zona.activa ? 'Activa' : 'Inactiva'}
                      </td>
                      <td className={propios.length === 0 ? 'apagado' : undefined}>
                        {propios.length === 0
                          ? 'ninguno — rige el global'
                          : propios
                              .map((t) => `${TIPO_VEHICULO[t.tipoVehiculo]} v${t.version}`)
                              .join(' · ')}
                      </td>
                    </tr>
                  );
                })}
              </Tabla>
            )}
          </Bloque>

          <Bloque
            titulo="Publicar una versión nueva"
            nota="Crea una versión nueva y cierra la vigente de esa clase y zona. No edita la que está: un viaje ya solicitado cita su tarifario y no puede cambiar de números después."
          >
            <div className="rejilla">
              <label className="campo">
                <span className="campo__etiqueta rotulo">Clase de vehículo</span>
                <select
                  className="selector"
                  value={form.tipoVehiculo}
                  disabled={enviando}
                  onChange={(evento) => {
                    if (esTipoVehiculo(evento.target.value)) {
                      escribir('tipoVehiculo', evento.target.value);
                    }
                  }}
                >
                  {TIPOS_VEHICULO_TODOS.map((tipo) => (
                    <option key={tipo} value={tipo}>
                      {TIPO_VEHICULO[tipo]}
                    </option>
                  ))}
                </select>
                <span className="campo__ayuda">
                  Se versiona por clase. Hoy hay una sola (AUTO): la clase mototaxi se retiró el
                  2026-09-01 y el servicio es sólo vehículos taxi.
                </span>
              </label>

              <label className="campo">
                <span className="campo__etiqueta rotulo">Zona</span>
                <select
                  className="selector"
                  value={form.zonaId}
                  disabled={enviando}
                  onChange={(evento) => escribir('zonaId', evento.target.value)}
                >
                  <option value="">Global (toda la operación)</option>
                  {/*
                    Las inactivas se listan pero NO se pueden elegir: el backend las rechaza con
                    400 (`La zona ... está inactiva`). Ocultarlas dejaría a quien las busca sin
                    saber por qué no aparecen; deshabilitarlas dice que existen y por qué no sirven.
                  */}
                  {zonas.map((zona) => (
                    <option key={zona.id} value={zona.id} disabled={!zona.activa}>
                      {zona.nombre}
                      {zona.activa ? '' : ' — inactiva, no admite tarifario'}
                    </option>
                  ))}
                </select>
                <span className="campo__ayuda">
                  Global es el único que usa el cálculo de tarifa hoy; uno por zona convive con él.
                </span>
              </label>

              <label className="campo">
                <span className="campo__etiqueta rotulo">Moneda</span>
                <input
                  className="entrada"
                  type="text"
                  maxLength={3}
                  value={form.moneda}
                  disabled={enviando}
                  onChange={(evento) => escribir('moneda', evento.target.value)}
                />
                <span
                  className="campo__ayuda"
                  style={mostrarErrores && errores.moneda ? { color: 'var(--rojo)' } : undefined}
                >
                  {(mostrarErrores ? errores.moneda : undefined) ??
                    'Código ISO-4217, 3 caracteres.'}
                </span>
              </label>

              {MONTOS.map((campo) => campoNumerico(campo))}
              {FACTORES.map((campo) => campoNumerico(campo))}
            </div>

            <p className="campo__ayuda" style={{ marginTop: 4 }}>
              Los montos se guardan con {DECIMALES} decimales (hasta {MAX_MONTO}); los factores,
              también con {DECIMALES} y hasta {MAX_FACTOR}. Un tercer decimal lo redondea la base de
              datos sin avisar.
            </p>

            {publicacion.estado === 'confirmando' || publicacion.estado === 'enviando' ? (
              <div className="bloque" style={{ marginTop: 14, marginBottom: 0 }}>
                <div className="bloque__cuerpo">
                  <p className="rotulo">Confirmar publicación</p>
                  <p className="pagina__ayuda" style={{ marginTop: 6 }}>
                    Vas a publicar <strong>{TIPO_VEHICULO[publicacion.cuerpo.tipoVehiculo]}</strong>{' '}
                    en <strong>{nombreDeZona(publicacion.cuerpo.zonaId ?? null)}</strong>.{' '}
                    {vigenteQueSeCierra
                      ? `Se cierra la versión v${vigenteQueSeCierra.version}, vigente desde ${laFechaHora(vigenteQueSeCierra.vigenteDesde)}, y la nueva rige desde este momento.`
                      : 'No hay ninguna versión vigente para esa combinación: ésta va a ser la v1.'}
                  </p>
                  <p className="campo__ayuda" style={{ marginTop: 0 }}>
                    Queda registrado en el log del backend con tu usuario, pero{' '}
                    <strong>no en una columna auditable</strong>: el modelo `Tarifario` no tiene
                    campo de operador ni de motivo. Es una brecha conocida, no un olvido de esta
                    pantalla.
                  </p>
                  <div className="fila" style={{ marginTop: 12 }}>
                    <button
                      type="button"
                      className="boton"
                      disabled={enviando}
                      onClick={() => setPublicacion({ estado: 'inactivo' })}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="boton boton--principal"
                      disabled={enviando}
                      onClick={() => confirmar(publicacion.cuerpo)}
                    >
                      {enviando ? 'Publicando…' : 'Confirmar publicación'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="fila" style={{ marginTop: 14 }}>
                <button
                  type="button"
                  className="boton boton--principal"
                  disabled={carga.estado !== 'listo'}
                  onClick={pedirConfirmacion}
                >
                  Revisar y publicar
                </button>
                {mostrarErrores && hayErrores ? (
                  <span className="apagado">Revisá los campos marcados.</span>
                ) : null}
              </div>
            )}

            {publicacion.estado === 'error' ? (
              <div style={{ marginTop: 14 }}>
                <ConError
                  titulo={
                    publicacion.concurrente
                      ? 'Otro operador publicó primero'
                      : 'No se pudo publicar'
                  }
                  detalle={
                    publicacion.concurrente
                      ? `${publicacion.mensaje} Lo que ves arriba ya es el tarifario que quedó vigente: revisalo antes de volver a publicar. Tus valores siguen cargados en el formulario.`
                      : publicacion.mensaje
                  }
                />
              </div>
            ) : publicacion.estado === 'listo' ? (
              <div style={{ marginTop: 14 }}>
                <p className="rotulo">Publicado</p>
                <p className="pagina__ayuda" style={{ marginTop: 4 }}>
                  {TIPO_VEHICULO[publicacion.resultado.tipoVehiculo]} en{' '}
                  {nombreDeZona(publicacion.resultado.zonaId)} quedó en{' '}
                  <strong>v{publicacion.resultado.version}</strong>, vigente desde{' '}
                  {laFechaHora(publicacion.resultado.vigenteDesde)}. Desde el próximo viaje el
                  pasajero ve el rango nuevo.
                </p>
              </div>
            ) : null}
          </Bloque>

          <p className="pagina__ayuda">
            Los límites geográficos de cada zona se dibujan sobre el mapa. Esa pantalla se hace
            cuando exista el servidor de teselas: dibujar un polígono sobre un mapa que no carga no
            sirve para nada.
          </p>
        </>
      )}
    </Pagina>
  );
}
